import React, { useState, useEffect } from 'react';
import { WifiOff, RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';

export default function OfflineIndicator() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showBanner, setShowBanner] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setIsReconnecting(false);
      if (wasOffline) {
        setShowBanner(true);
        // Auto-hide the "back online" banner after 5 seconds
        setTimeout(() => {
          setShowBanner(false);
          setWasOffline(false);
        }, 5000);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowBanner(true);
      setWasOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial check
    if (!navigator.onLine) {
      setShowBanner(true);
      setWasOffline(true);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline]);

  const handleRetry = async () => {
    setIsReconnecting(true);
    try {
      // Try to fetch a simple endpoint to check connectivity
      const response = await fetch('/api/health', { 
        method: 'HEAD',
        cache: 'no-store'
      });
      if (response.ok) {
        window.location.reload();
      }
    } catch (error) {
      setIsReconnecting(false);
    }
  };

  if (!showBanner) {
    return null;
  }

  return (
    <div
      className={`fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 transition-all duration-300 ${
        isOnline 
          ? 'bg-green-600 text-white' 
          : 'bg-slate-800 text-white border border-slate-700'
      }`}
      style={{ maxWidth: '90vw' }}
      data-testid="offline-indicator"
    >
      {isOnline ? (
        <>
          <CheckCircle size={20} className="flex-shrink-0" />
          <span className="text-sm font-medium">Back online! Your data will sync automatically.</span>
        </>
      ) : (
        <>
          <WifiOff size={20} className="flex-shrink-0 text-red-400" />
          <div className="flex-1">
            <p className="text-sm font-medium">You're offline</p>
            <p className="text-xs text-slate-400">Some features may be limited</p>
          </div>
          <button
            onClick={handleRetry}
            disabled={isReconnecting}
            className="flex items-center gap-1 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded text-xs font-medium transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={isReconnecting ? 'animate-spin' : ''} />
            {isReconnecting ? 'Checking...' : 'Retry'}
          </button>
        </>
      )}
    </div>
  );
}

// Hook for checking online status in other components
export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}

// Component for showing cached data indicator
export function CachedDataBadge({ isCached }) {
  if (!isCached) return null;
  
  return (
    <span 
      className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium"
      title="This data was loaded from cache and may be outdated"
    >
      <AlertTriangle size={12} />
      Cached
    </span>
  );
}
